import React, {useDebugValue} from 'react';

import classNames from 'classnames';

import {useSearchFormContext} from '../../../hooks';
import {undef, hasValidDestinationValues} from '../../../utils';

import {SearchIcon} from '../../SearchIcon';

const componentClassNamespace = 'search-form';
const componentBaseClassName = `${componentClassNamespace}__field`;
const componentBaseHighlightClassName = `${componentBaseClassName}--highlight`;
const componentClassName = `${componentClassNamespace}__destination-field`;

// eslint-disable-next-line complexity
function useComponent() {
    const {
        system: {
            options: {
                site,
                baseUrl,
                appPrefix,
                locale
            }
        },
        media: {
            settings: {
                isMobile
            }
        },
        form: {
            options: {
                autoProgress,
                showIcons
            }
        },
        destination: {
            actions: {
                setIsTypeaheadInputFocus,
                setAllValues
            },
            events: {
                onBlur,
                onFocus,
                onMount,
                onSearchSelected
            },
            options: {
                allowHighlight,
                filterType,
                googlePlaceApiKey,
                googlePlaceApiUrl,
                hideSelectedFilter,
                highlightMode,
                iconName,
                id,
                matchMode,
                minLength,
                popoverDesktopMaxWidth,
                popoverDesktopMinMarginRight,
                popoverDesktopMinWidth,
                popoverDesktopWidth,
                popoverMaxDisplayCount,
                popoverMaxDisplayCountMobile,
                recentSearchesDisplayMax,
                remotePath,
                retainUserInput,
                showIcon,
                showInlineSuggestion,
                showRecentSearches,
                showResultIcons,
                showResultLabels,
                showSpinner,
                showTrendingSuggestions,
                truncateResultTerms,
                usePopoverAnimation,
                useResultsAnimation
            },
            settings: {
                isTypeaheadInputFocus
            },
            translations: {
                clearSearchLabel,
                noResultsFoundMessage,
                placeholderMessage,
                recentSearchesMessage,
                recentlyViewedLabel,
                resultsFoundMessage,
                selectedMessage,
                trendingDestinationsMessage,
                typeAirportLabel,
                typeCityLabel,
                typeLandmarkLabel,
                typeMetroStationLabel,
                typeNeighborhoodLabel,
                typeRegionLabel,
                typeTrainStationLabel,
                whereLabel
            },
            latestValues,
            latestValues: {
                destination: search
            }
        },
        dates: {
            actions: {
                setIsOpen: setDatesIsOpen
            }
        },
        guests: {
            actions: {
                setIsOpen: setGuestsIsOpen
            }
        }
    } = useSearchFormContext();

    const className = classNames(componentBaseClassName, {
        [componentBaseHighlightClassName]: !isMobile && allowHighlight && !hasValidDestinationValues(latestValues)
    }, componentClassName);

    const icon = (showIcon || showIcons && showIcon === undef) && (
        <SearchIcon name={iconName}/>
    ) || null;

    const messages = {
        clearsearch: clearSearchLabel,
        destinationplaceholdermsg: placeholderMessage,
        destinationselected: selectedMessage,
        noresultsfound: noResultsFoundMessage,
        recentlyviewed: recentlyViewedLabel,
        recentsearches: recentSearchesMessage,
        resultsfound: resultsFoundMessage,
        trendingdestinations: trendingDestinationsMessage,
        typeairport: typeAirportLabel,
        typecity: typeCityLabel,
        typelandmark: typeLandmarkLabel,
        typemetrostation: typeMetroStationLabel,
        typeneighborhood: typeNeighborhoodLabel,
        typeregion: typeRegionLabel,
        typetrainstation: typeTrainStationLabel,
        where: whereLabel
    };

    /* istanbul ignore next */
    function handleBlur(...args) {
        onBlur(...args);
    }

    function handleFocus(...args) {
        if (autoProgress) {
            setDatesIsOpen(false);
            setGuestsIsOpen(false);
        }

        onFocus(...args);
    }

    function handleMount(...args) {
        onMount(...args);
    }

    function handleSearchSelected(...args) {
        // TODO: Move this function to handleBlur
        setIsTypeaheadInputFocus(false);

        if (autoProgress && !isMobile) {
            setTimeout(function delayedDatesOpen() {
                setDatesIsOpen(true);
            }, 300);
        }

        const [
            changedValues
        ] = args;

        setAllValues(changedValues);

        onSearchSelected(...args);
    }

    const result = {
        className,
        appPrefix,
        baseUrl,
        filterType,
        googlePlaceApiKey,
        googlePlaceApiUrl,
        hideSelectedFilter,
        highlightMode,
        icon,
        id,
        isMobile,
        isTypeaheadInputFocus,
        locale,
        matchMode,
        messages,
        minLength,
        onBlur: handleBlur,
        onFocus: handleFocus,
        onMount: handleMount,
        onSearchSelected: handleSearchSelected,
        popoverDesktopMaxWidth,
        popoverDesktopMinMarginRight,
        popoverDesktopMinWidth,
        popoverDesktopWidth,
        popoverMaxDisplayCount,
        popoverMaxDisplayCountMobile,
        recentSearchesDisplayMax,
        remotePath,
        retainUserInput,
        search,
        showInlineSuggestion,
        showRecentSearches,
        showResultIcons,
        showResultLabels,
        showSpinner,
        showTrendingSuggestions,
        site,
        truncateResultTerms,
        usePopoverAnimation,
        useResultsAnimation
    };

    useDebugValue(result);

    return result;
}

export {useComponent};
